import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo
} from "react";

import {
    AnalyticsEventPayload,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";
import { useFireCrossFlowAnalyticsEvents } from "@atlassian/surface-analytics";
import { ProductKeys } from "@atlassiansox/asc-stargate";
import {
    Journeys,
    TargetType,
    useCrossFlow
} from "@atlassiansox/cross-flow-support";

import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useAnalyticsClient } from "../../../analytics/wac-analytics/analytics-client";

import {
    CrossFlowComponentViewedFunction,
    CrossFlowFrontendContextType,
    CrossFlowFrontendTouchpointOptions,
    OpenCrossFlowFunction,
    OpenCrossFlowFunctionParams
} from "./types";
import {
    WAC_GENERIC_TOUCHPOINT_COMPONENT_NAME,
    WAC_GENERIC_TOUCHPOINT_NAME,
    getRolloutFlagValue
} from "./utils";

const CrossFlowFrontendContext = createContext<CrossFlowFrontendContextType>({
    isCrossFlowEnabled: false
});

export const CrossFlowFrontendProvider = ({
    children,
    productKeys: providedProductKeys,
    productKey,
    screenName,
    viewedOnMount = true
}: PropsWithChildren<CrossFlowFrontendTouchpointOptions>) => {
    const productKeys = useMemo(() => {
        if (providedProductKeys) {
            return providedProductKeys;
        }
        if (productKey) {
            return [productKey];
        }
        return;
    }, [productKey, providedProductKeys]);

    if (!productKeys?.length) {
        throw new Error(
            "CrossFlowFrontendProvider requires either productKeys or productKey to be provided"
        );
    }

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const { fireViewedScreenEvent } = useFireCrossFlowAnalyticsEvents();
    const { isUserLoggedIn } = useLoggedInUser();
    const crossFlow = useCrossFlow();

    const attributes = useMemo(() => {
        return {
            productKeys,
            productKey,
            screenName,
            isUserLoggedIn,
            crossFlowEnabled: crossFlow?.isEnabled,
            isConfluenceEnabled: getRolloutFlagValue(ProductKeys.CONFLUENCE),
            isJiraSoftwareEnabled: getRolloutFlagValue(
                ProductKeys.JIRA_SOFTWARE
            ),
            isJiraProductDiscoveryEnabled: getRolloutFlagValue(
                ProductKeys.JIRA_PRODUCT_DISCOVERY
            )
        };
    }, [
        productKeys,
        productKey,
        screenName,
        isUserLoggedIn,
        crossFlow?.isEnabled
    ]);
    const fireEvent = useCallback(
        (eventPayload: AnalyticsEventPayload) => {
            analyticsClient &&
                createAnalyticsEvent({
                    ...eventPayload,
                    attributes
                }).fire();
        },
        [analyticsClient, createAnalyticsEvent, attributes]
    );

    const fireTouchpointViewedEvent = useCallback(() => {
        if (!analyticsClient) {
            return;
        }
        if (screenName) {
            fireViewedScreenEvent(
                screenName,
                {
                    recommendedProductIds: productKeys
                },
                attributes
            );
            return;
        }
        fireEvent({
            action: "viewed",
            actionSubject: WAC_GENERIC_TOUCHPOINT_NAME,
            eventType: "ui"
        });
    }, [
        analyticsClient,
        screenName,
        fireEvent,
        fireViewedScreenEvent,
        productKeys,
        attributes
    ]);

    const isCrossFlowEnabled = useMemo(() => {
        if (
            !productKeys?.length ||
            !screenName ||
            !crossFlow?.isEnabled ||
            !isUserLoggedIn
        ) {
            fireEvent({
                action: "disabled",
                actionSubject: "crossFlowFrontend",
                eventType: "operational"
            });
            return false;
        }
        return true;
    }, [
        crossFlow?.isEnabled,
        fireEvent,
        isUserLoggedIn,
        productKeys?.length,
        screenName
    ]);

    useEffect(() => {
        if (viewedOnMount) {
            fireTouchpointViewedEvent();
        }
    }, [fireTouchpointViewedEvent, viewedOnMount]);

    return (
        <CrossFlowFrontendContext.Provider
            value={{
                isCrossFlowEnabled,
                productKeys,
                screenName,
                fireTouchpointViewedEvent,
                productKey
            }}
        >
            {children}
        </CrossFlowFrontendContext.Provider>
    );
};

export const useCrossFlowFrontend = (
    fallbackHref?: string,
    fallbackParams?: OpenCrossFlowFunctionParams,
    viewedOnMount = true
) => {
    const context = useContext(CrossFlowFrontendContext);

    if (!context) {
        throw new Error(
            "useCrossFlowFrontend must be used within a CrossFlowFrontendProvider"
        );
    }

    const {
        isCrossFlowEnabled,
        screenName,
        productKeys,
        productKey: providedProductKey
    } = context;

    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const crossFlow = useCrossFlow();
    const { fireInteractedUIEvent, fireViewedUIEvent } =
        useFireCrossFlowAnalyticsEvents();

    const fireEvent = useCallback(
        (eventPayload: AnalyticsEventPayload) => {
            analyticsClient && createAnalyticsEvent(eventPayload).fire();
        },
        [analyticsClient, createAnalyticsEvent]
    );

    const handleRedirect = useCallback(
        (href?: string) => {
            if (typeof href !== "string") {
                fireEvent({
                    action: "failed",
                    actionSubject: "crossFlowFallbackRedirectAttempt",
                    eventType: "operational",
                    attributes: {
                        errorMessage: "Fallback href is not a string"
                    }
                });
                return;
            }
            fireEvent({
                action: "successful",
                actionSubject: "crossFlowFallbackRedirectAttempt",
                eventType: "operational"
            });
            window.location.href = href;
        },
        [fireEvent]
    );

    const fireCrossFlowComponentInteractedEvent = useCallback(
        ({
            componentType = "button",
            interactionName = "tryItFreeButton",
            productKey
        }: OpenCrossFlowFunctionParams) => {
            if (screenName && productKey) {
                fireInteractedUIEvent(
                    {
                        action: "clicked",
                        actionSubject: componentType,
                        actionSubjectId: interactionName
                    },
                    {
                        source: screenName,
                        targetProductId: productKey
                    }
                );
            } else {
                fireEvent({
                    action: "clicked",
                    actionSubject: componentType,
                    actionSubjectId: interactionName,
                    eventType: "track"
                });
            }
        },
        [fireEvent, fireInteractedUIEvent, screenName]
    );

    const openCrossFlow: OpenCrossFlowFunction = useCallback(
        async (event?: React.MouseEvent, params = {}) => {
            const {
                productKey = providedProductKey,
                href = fallbackHref,
                edition = fallbackParams?.edition,
                interactionName = "tryItFreeButton"
            } = params;
            try {
                if (
                    !crossFlow.isEnabled ||
                    !isCrossFlowEnabled ||
                    !productKey
                ) {
                    handleRedirect(href);
                    return;
                }
                fireCrossFlowComponentInteractedEvent(params);

                if (getRolloutFlagValue(productKey, true) !== "variation") {
                    handleRedirect(href);
                    return;
                }

                if (event) {
                    event.preventDefault();
                }

                fireEvent({
                    action: "successful",
                    actionSubject: "crossFlowRequest",
                    eventType: "operational"
                });

                await crossFlow.api.open({
                    journey: Journeys.GET_STARTED,
                    targetProduct: productKey as TargetType,
                    sourceComponent: screenName || WAC_GENERIC_TOUCHPOINT_NAME,
                    sourceContext: interactionName,
                    extensions: {
                        edition
                    }
                });
            } catch (error) {
                const errorData = error as Error;
                fireEvent({
                    action: "failed",
                    actionSubject: "crossFlowRequest",
                    eventType: "operational",
                    attributes: {
                        errorMessage: errorData?.message,
                        errorStack: errorData?.stack
                    }
                });
                handleRedirect(href);
            }
        },
        [
            providedProductKey,
            fallbackHref,
            fallbackParams?.edition,
            crossFlow,
            isCrossFlowEnabled,
            fireCrossFlowComponentInteractedEvent,
            fireEvent,
            screenName,
            handleRedirect
        ]
    );

    const fireCrossFlowComponentViewedEvent: CrossFlowComponentViewedFunction =
        useCallback(
            (params = {}) => {
                const { componentType = "button", componentName } = params;
                if (componentName && screenName && productKeys) {
                    fireViewedUIEvent(
                        {
                            action: "viewed",
                            actionSubject: componentType,
                            actionSubjectId: componentName
                        },
                        {
                            source: screenName,
                            recommendedProductIds: productKeys
                        }
                    );
                } else {
                    fireEvent({
                        action: "viewed",
                        actionSubject: WAC_GENERIC_TOUCHPOINT_COMPONENT_NAME,
                        eventType: "ui"
                    });
                }
            },
            [fireEvent, fireViewedUIEvent, productKeys, screenName]
        );

    useEffect(() => {
        if (isCrossFlowEnabled && viewedOnMount) {
            fireCrossFlowComponentViewedEvent();
        }
    }, [fireCrossFlowComponentViewedEvent, isCrossFlowEnabled, viewedOnMount]);

    return {
        isCrossFlowEnabled,
        openCrossFlow,
        fireCrossFlowComponentViewedEvent
    };
};
